/**
 * Post-password-reset session isolation.
 *
 * Completing an MA password setup/reset must never inherit a pre-existing
 * MA SPA identity (cross-user or same-user). Auth0 password change alone is
 * not a login — the app must force a fresh authentication decision.
 *
 * Lifecycle (one-time gate):
 *   RESET COMPLETE
 *     → invalidate prior MA SPA identity (+ Auth0 token cache)
 *     → mark require-fresh-login
 *     → Auth0 interactive login (prompt=login)
 *     → successful /callback
 *     → consume require-fresh-login exactly once
 *     → resolve MA context /app
 *
 * CRITICAL: never clear Auth0 SPA localStorage cache from isLoggedIn() /
 * clearLocalSession(). Doing so destroys tokens just written by
 * handleRedirectCallback and causes a /signin ↔ Auth0 redirect loop.
 */

/** sessionStorage flag: block navigate-app until a fresh Auth0 callback succeeds. */
export const MA_REQUIRE_FRESH_LOGIN_KEY = "kk_ma_require_fresh_login";

export function markRequireFreshLogin(
  storage = typeof sessionStorage !== "undefined" ? sessionStorage : null,
) {
  try {
    storage?.setItem(MA_REQUIRE_FRESH_LOGIN_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function isRequireFreshLogin(
  storage = typeof sessionStorage !== "undefined" ? sessionStorage : null,
) {
  try {
    return storage?.getItem(MA_REQUIRE_FRESH_LOGIN_KEY) === "1";
  } catch {
    return false;
  }
}

/** Consume after a qualifying successful Auth0 callback — not before. */
export function consumeRequireFreshLogin(
  storage = typeof sessionStorage !== "undefined" ? sessionStorage : null,
) {
  const required = isRequireFreshLogin(storage);
  try {
    storage?.removeItem(MA_REQUIRE_FRESH_LOGIN_KEY);
  } catch {
    /* ignore */
  }
  return required;
}

/**
 * Remove Auth0 SPA JS localStorage token cache so getTokenSilently cannot
 * revive a prior user. Does NOT touch sessionStorage `a0.spajs.txs.*`
 * (login transaction) — that must survive until /callback.
 *
 * Call only from the intentional post-reset / logout path — never from
 * isLoggedIn() while a callback may be in flight.
 */
export function clearAuth0SpaLocalCache(
  storage = typeof localStorage !== "undefined" ? localStorage : null,
) {
  if (!storage) return;
  try {
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith("@@auth0spajs@@")) {
        keys.push(key);
      }
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    /* ignore */
  }
}

/**
 * Pure decision: after password-changed invalidation, /signin must force
 * interactive Auth0 login even if a residual MA token looks present.
 */
export function resolveFreshLoginGateAction({
  isLoggedIn,
  requireFreshLogin,
}) {
  if (requireFreshLogin) {
    return { type: "auth0-redirect", prompt: "login" };
  }
  if (isLoggedIn) {
    return { type: "navigate-app", path: "/app" };
  }
  return { type: "auth0-redirect" };
}

/**
 * Pure decision: when to clear the fresh-login gate relative to callback.
 * Gate must survive failed callbacks; clear only on success.
 */
export function shouldConsumeFreshLoginGate({ callbackSucceeded }) {
  return Boolean(callbackSucceeded);
}
